/**
 * Route paths for the learner-facing and public parts of the app.
 *
 * Mirrors the convention already used by `ADMIN_ROUTE_PATH` /
 * `ADMIN_ROUTE_RELATIVE_PATH` in `@/administration/constants/adminRoutePaths`:
 *
 * - `ROUTE_PATH` holds absolute paths. Use it for navigation targets,
 *   `matchPath` patterns and any `<Route>` declared with an absolute path.
 * - `ROUTE_RELATIVE_PATH` holds the path fragments used by nested `<Route>`
 *   declarations in `App.tsx`, where react-router resolves them against the
 *   parent route.
 *
 * Entries suffixed with `_BASE` are prefixes meant to be composed with dynamic
 * segments at the call site, e.g.
 * `` `${ROUTE_PATH.JOURNEY_BASE}/${id}/${slugify(title)}` ``.
 *
 * Administration (`/administration/*`) and the manager analytics pages
 * (`/management/analyze/*`) are owned by `adminRoutePaths.ts` and
 * `managerRoutePaths.ts` respectively and are intentionally not repeated here.
 */
export const ROUTE_PATH = {
  HOME: '/',
  NOT_FOUND: '/404',
  LOGIN: '/login',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  ABOUT: '/about',
  ONBOARDING: '/onboarding',
  NOTIFICATIONS: '/notifications',
  INJECT_REFLECTION: '/inject-reflection',

  MANAGEMENT: '/management',
  MANAGER_USER: '/users/:id',
  MANAGER_ANALYTICS: '/analytics/*',

  CATALOG: '/learning',
  CATALOG_EVENTS: '/learning/events',
  CATALOG_SEARCH: '/learning/search',
  CATALOG_PROVIDERS: '/learning/provider',
  CATALOG_PROVIDER: '/learning/provider/:provider',
  CATALOG_CATEGORY: '/learning/category/:category',
  CATALOG_SUBJECT: '/learning/category/:category/subject/:subject',
  CATALOG_PROVIDER_BASE: '/learning/provider',
  CATALOG_CATEGORY_BASE: '/learning/category',

  LEARNING: '/learning/:id',
  /** Prefix-match patterns for `matchPath`, which ignore the vanity name segment. */
  LEARNING_MATCH: '/learning/:id/*',
  COURSE_DETAILS_MATCH: '/learning/course/details/:id/*',
  JOURNEY_MATCH: '/journey/:journeyId/*',
  LEARNING_NAMED: '/learning/:id/:learningName/*',
  LEARNING_BASE: '/learning',
  E_LEARNING_VIEW: '/learning/e-learning/:id/view',
  E_LEARNING_VIEW_NAMED: '/learning/e-learning/:id/:learningName/view',
  E_LEARNING_BASE: '/learning/e-learning',

  /** Legacy product / course detail pages */
  COURSE: '/learning/course/:id/:courseName/*',
  COURSE_DETAILS: '/learning/course/details/:id/:courseName/*',
  COURSE_BASE: '/learning/course',
  COURSE_DETAILS_BASE: '/learning/course/details',

  COLLECTION: '/learning-collection/:collectionId/:collectionName',
  COLLECTION_LEARNING: '/learning-collection/:collectionId/:collectionName/learning/:id',
  COLLECTION_LEARNING_NAMED: '/learning-collection/:collectionId/:collectionName/learning/:id/:learningName/*',
  COLLECTION_COURSE: '/learning-collection/:collectionId/:collectionName/learning/course/:id/:courseName/*',
  COLLECTION_COURSE_DETAILS: '/learning-collection/:collectionId/:collectionName/learning/course/details/:id/:courseName/*',
  COLLECTION_ARTICLE: '/learning-collection/:collectionId/:collectionName/:type/:id/:articleName/*',
  COLLECTION_BASE: '/learning-collection',

  JOURNEY: '/journey/:journeyId/:name/*',
  JOURNEY_SECTION: '/journey/:journeyId/:name/section/:sectionId',
  JOURNEY_SESSION_PARTICIPANTS: '/journey/:journeyId/:name/sessions/:sessionId/participants',
  JOURNEY_DISCUSSIONS: '/journey/:journeyId/:name/discussions/*',
  JOURNEY_DISCUSSIONS_TEAMS_SETUP: '/journey/:journeyId/:name/discussionsTeamsSetup',
  JOURNEY_DISCUSSIONS_TEAMS_IMPORT: '/journey/:journeyId/:name/discussionsTeamsImport',
  JOURNEY_CERTIFICATE: '/journey/:journeyId/:name/certificate',
  JOURNEY_BASE: '/journey',

  ASSIGNMENT: '/assignment/:id/:title/*',
  ASSIGNMENT_PARTICIPANTS: '/assignment/:id/:title/participants',
  ASSIGNMENT_BASE: '/assignment',

  POST: '/post/:postId/*',
  REFLECTION: '/reflection/:postId/*',
  POST_BASE: '/post',
  REFLECTION_BASE: '/reflection',

  PROFILE: '/user/profile',
  MY_LEARNING: '/user/my-learning',
  MY_LEARNING_ALL: '/user/my-learning/*',
  MY_LEARNING_ONGOING: '/user/my-learning/ongoing',
  MY_LEARNING_ASSIGNED: '/user/my-learning/assigned',
  MY_LEARNING_SAVED_BY_ME: '/user/my-learning/saved-by-me',
  MY_LEARNING_COMPLETED: '/user/my-learning/completed',
  MY_LEARNING_HR_ONLINE_DATA: '/user/my-learning/hr-online-data',

  SUPER_ADMIN: '/super-admin',
  SUPER_ADMIN_STYLEGUIDE: '/super-admin/styleguide',
  SUPER_ADMIN_STYLEGUIDE_SECTION: '/super-admin/styleguide/:section',
  SUPER_ADMIN_CHECKLIST: '/super-admin/checklist',
  STYLEGUIDE: '/styleguide',
  STYLEGUIDE_SECTION: '/styleguide/:section',
};

export const ROUTE_RELATIVE_PATH = {
  HOME: '',
  HOME_SLASH: '/',
  WILDCARD: '*',
  NOT_FOUND: '404',
  LOGIN: 'login',
  TERMS: 'terms',
  PRIVACY: 'privacy',
  ABOUT: 'about',
  ONBOARDING: 'onboarding',
  NOTIFICATIONS: 'notifications',
  INJECT_REFLECTION: 'inject-reflection',

  MANAGEMENT: 'management',
  MANAGER_ANALYTICS: 'analytics/*',

  /** Nested under `learning` */
  LEARNING_ROOT: 'learning',
  LEARNING: ':id',
  LEARNING_NAMED: ':id/:learningName/*',
  E_LEARNING_VIEW: 'e-learning/:id/view',
  E_LEARNING_VIEW_NAMED: 'e-learning/:id/:learningName/view',
  CATALOG_EVENTS: 'events',
  CATALOG_SEARCH: 'search',
  CATALOG_PROVIDERS: 'provider',
  CATALOG_PROVIDER: 'provider/:provider',
  CATALOG_CATEGORY: 'category/:category',
  CATALOG_SUBJECT: 'category/:category/subject/:subject',
  COURSE: 'course/:id/:courseName/*',
  COURSE_DETAILS: 'course/details/:id/:courseName/*',

  /** Nested under `learning-collection/:collectionId/:collectionName` */
  COLLECTION_ROOT: 'learning-collection/:collectionId/:collectionName',
  COLLECTION_LEARNING: 'learning/:id',
  COLLECTION_LEARNING_NAMED: 'learning/:id/:learningName/*',
  COLLECTION_COURSE: 'learning/course/:id/:courseName/*',
  COLLECTION_COURSE_DETAILS: 'learning/course/details/:id/:courseName/*',
  COLLECTION_ARTICLE: ':type/:id/:articleName/*',

  /**
   * Journeys. The certificate and the team-import wizard are spelled out in full: both render
   * outside `LayoutWrapper`, so they are declared beside `JOURNEY_ROOT` rather than within it.
   */
  JOURNEY_CERTIFICATE: 'journey/:journeyId/:name/certificate',
  JOURNEY_DISCUSSIONS_TEAMS_IMPORT: 'journey/:journeyId/:name/discussionsTeamsImport',
  JOURNEY_ROOT: 'journey/:journeyId/:name/*',
  JOURNEY_SECTION: 'section/:sectionId',
  JOURNEY_SESSION_PARTICIPANTS: 'sessions/:sessionId/participants',
  JOURNEY_DISCUSSIONS: 'discussions/*',
  JOURNEY_DISCUSSIONS_TEAMS_SETUP: 'discussionsTeamsSetup',

  ASSIGNMENT_ROOT: 'assignment/:id/:title/*',
  ASSIGNMENT_PARTICIPANTS: 'participants',

  STYLEGUIDE: 'styleguide',
  STYLEGUIDE_SECTION: 'styleguide/:section',
  SUPER_ADMIN_CHECKLIST: 'checklist',
  SUPER_ADMIN_STYLEGUIDE_SECTION: ':section',
};
